import React from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { GripVertical, MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type CallDisposition from "@/model/interface/CallDisposition";
import { DISPOSITION_ACTION_BADGES } from "@/util/dispositions";

interface DispositionRowProps {
  disposition: CallDisposition;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, id: string) => void;
}

const DispositionRow: React.FC<DispositionRowProps> = ({
  disposition,
  onEdit,
  onDelete,
  onToggleActive,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const actionBadge = disposition.action ? DISPOSITION_ACTION_BADGES[disposition.action] : null;

  return (
    <div
      className="flex items-center gap-3 py-3 px-1"
      draggable
      onDragStart={(e) => onDragStart(e, disposition.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, disposition.id)}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0 cursor-grab active:cursor-grabbing" />
      <span
        className="h-4 w-4 rounded-full shrink-0"
        style={{ backgroundColor: disposition.color }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{disposition.name}</span>
          {disposition.requiresFollowup && (
            <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
              Follow-up
            </span>
          )}
          {disposition.isDefault && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
              Default
            </span>
          )}
          {actionBadge && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{actionBadge}</span>
          )}
          {disposition.dealStage?.name && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
              Deal to {disposition.dealStage.name}
            </span>
          )}
        </div>
        {disposition.description && (
          <p className="text-xs text-muted-foreground truncate">
            {disposition.description}
          </p>
        )}
      </div>
      <Switch
        checked={disposition.isActive}
        onCheckedChange={onToggleActive}
        className="shrink-0"
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={onEdit}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Options">
            <MoreVertical className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem className="text-destructive" onClick={onDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete disposition
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DispositionRow;
